import { m as motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  GraduationCap,
  Rocket,
  Sparkles,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { UgaHeroIllustration } from "@/components/uga-page-illustration";
import { NEXT_COHORT_PATH } from "@/lib/cohorts";
import { EXTERNAL_LINKS } from "@/lib/constants";
import { defaultViewport } from "@/lib/motion";
import {
  UGA_DISCOUNT_CTA,
  UGA_DISCOUNT_BODY_CODE,
  UGA_DISCOUNT_BODY_MIDDLE,
  UGA_DISCOUNT_BODY_SUFFIX,
  UGA_DISCOUNT_HEADLINE,
  UGA_FEATURE_GROUPS,
  UGA_FEATURES_SECTION_BADGE,
  UGA_FEATURES_SECTION_HEADLINE,
  UGA_FEATURES_SECTION_SUBHEAD,
  UGA_HERO_BADGE,
  UGA_HERO_SUBHEAD,
  UGA_HERO_TITLE_ACCENT,
  UGA_HERO_TITLE_LEAD,
  UGA_NEIL_TRUST_AFTER_NAME,
  UGA_PAGE_SEO_DESCRIPTION,
  UGA_PAGE_TITLE,
  UGA_SECTIONS,
  UGA_STORY_SECTION_EYEBROW,
  UGA_STORY_SECTION_HEADLINE,
  UGA_STORY_SECTION_SUBHEAD,
} from "@/lib/uga-page-content";
import {
  formatUgaPrice,
  UGA_PREMIUM_DISCOUNTED_PRICE,
  UGA_PREMIUM_LIST_PRICE,
  UGA_STUDENT_DISCOUNT_CODE,
  UGA_STUDENT_DISCOUNT_LABEL,
  UGA_STUDENT_PAGE_PATH,
} from "@/lib/uga-offer";
import { useSEO } from "@/lib/use-seo";

const cardBorderColors = [
  "border-l-primary",
  "border-l-secondary",
  "border-l-accent",
] as const;

const sectionIconStyles = [
  {
    Icon: BookOpen,
    badge: "bg-primary/10 text-primary",
  },
  {
    Icon: Users,
    badge: "bg-secondary/10 text-secondary",
  },
  {
    Icon: Rocket,
    badge: "bg-accent/10 text-accent",
  },
] as const;

const cardClassName = "glass-card p-6 md:p-8 border-l-4" as const;

const illustrationHoverSpring = {
  type: "spring",
  stiffness: 380,
  damping: 14,
  mass: 0.7,
} as const;

const UgaStudentsPage = () => {
  const shouldReduceMotion = useReducedMotion();

  useSEO({
    title: UGA_PAGE_TITLE,
    description: UGA_PAGE_SEO_DESCRIPTION,
    path: UGA_STUDENT_PAGE_PATH,
    noindex: true,
  });

  return (
    <div className="min-h-screen">
      <section className="relative overflow-hidden bg-gradient-mesh pt-36 md:pt-32 pb-10 md:pb-14">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-96 h-96 bg-gradient-radial-primary opacity-30 blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-gradient-radial-secondary opacity-30 blur-3xl" />
        </div>
        <div className="section-inner relative z-10 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="md:grid md:grid-cols-[1fr_auto] md:gap-10 md:items-center"
          >
            <div className="space-y-5 text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold">
                <GraduationCap className="w-4 h-4" />
                {UGA_HERO_BADGE}
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold">
                {UGA_HERO_TITLE_LEAD}{" "}
                <span className="bg-gradient-to-r from-[hsl(var(--primary))] via-[hsl(var(--secondary))] to-[hsl(var(--accent))] bg-clip-text text-transparent">
                  {UGA_HERO_TITLE_ACCENT}
                </span>
              </h1>
              <p className="text-lg md:text-xl text-foreground/80 max-w-2xl leading-relaxed">
                {UGA_HERO_SUBHEAD}
              </p>
            </div>
            <div className="mt-6 md:mt-0 flex justify-center md:justify-end shrink-0 overflow-visible p-4 md:p-2">
              <motion.div
                className="origin-center"
                whileHover={
                  shouldReduceMotion
                    ? undefined
                    : { scale: 1.1, rotate: 2.5 }
                }
                transition={illustrationHoverSpring}
              >
                <UgaHeroIllustration className="w-full max-w-sm h-auto max-h-[160px] md:max-h-[180px]" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh opacity-10 pointer-events-none" />
        <div className="section-inner relative z-10 max-w-3xl mx-auto py-12 md:py-16">
          <motion.header
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={defaultViewport}
            transition={{ duration: 0.45 }}
            className="text-center mb-10 md:mb-12 space-y-3"
          >
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">
              {UGA_STORY_SECTION_EYEBROW}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold">
              <span className="bg-gradient-to-r from-[hsl(var(--primary))] via-[hsl(var(--secondary))] to-[hsl(var(--accent))] bg-clip-text text-transparent">
                {UGA_STORY_SECTION_HEADLINE}
              </span>
            </h2>
            <p className="text-foreground/70 max-w-xl mx-auto leading-relaxed">
              {UGA_STORY_SECTION_SUBHEAD}
            </p>
          </motion.header>

          <div className="space-y-6">
            {UGA_SECTIONS.map((section, index) => {
              const { Icon, badge } = sectionIconStyles[index];
              return (
                <motion.article
                  key={section.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={defaultViewport}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  className={`group ${cardClassName} hover-lift ${cardBorderColors[index % cardBorderColors.length]}`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`shrink-0 rounded-xl p-3 ${badge} transition-transform group-hover:scale-110`}
                      aria-hidden="true"
                    >
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="space-y-3 min-w-0">
                      <h3 className="text-xl md:text-2xl font-bold leading-tight">
                        {section.title}
                      </h3>
                      <p className="text-foreground/80 leading-relaxed">
                        {section.body}
                      </p>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-foreground/5 border-y border-foreground/5">
        <div className="section-inner max-w-3xl mx-auto py-12 md:py-16 space-y-8">
          <motion.header
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={defaultViewport}
            transition={{ duration: 0.45 }}
            className="text-center space-y-3"
          >
            <div className="inline-flex items-center justify-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-primary">
              <Sparkles className="w-4 h-4" aria-hidden="true" />
              <span className="text-sm font-semibold">{UGA_FEATURES_SECTION_BADGE}</span>
            </div>
            <h2
              id="uga-features-heading"
              className="text-3xl md:text-4xl font-bold"
            >
              {UGA_FEATURES_SECTION_HEADLINE}
            </h2>
            <p className="text-foreground/70 max-w-xl mx-auto leading-relaxed">
              {UGA_FEATURES_SECTION_SUBHEAD}
            </p>
          </motion.header>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={defaultViewport}
            transition={{ duration: 0.5 }}
            className={`${cardClassName} border-l-primary space-y-8`}
          >
            {UGA_FEATURE_GROUPS.map((group) => (
              <div key={group.label} className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                  {group.label}
                </p>
                <ul className="space-y-2.5" aria-labelledby="uga-features-heading">
                  {group.items.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-foreground/80 leading-relaxed">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </motion.div>

          <motion.aside
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={defaultViewport}
            transition={{ duration: 0.5, delay: 0.05 }}
            className={`${cardClassName} border-l-secondary`}
          >
            <p className="text-foreground/80 leading-relaxed text-sm md:text-base">
              <Link
                to="/about-us"
                className="font-semibold text-foreground hover:text-primary transition-colors underline underline-offset-4"
              >
                Neil Morelli, PhD
              </Link>
              {UGA_NEIL_TRUST_AFTER_NAME}
            </p>
          </motion.aside>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="section-inner">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={defaultViewport}
            transition={{ duration: 0.5 }}
            className={`${cardClassName} border-l-accent max-w-xl mx-auto text-center space-y-5`}
          >
            <h2 className="text-2xl md:text-3xl font-bold">
              {UGA_DISCOUNT_HEADLINE}
            </h2>
            <p className="text-foreground/80 leading-relaxed">
              Premium is {formatUgaPrice(UGA_PREMIUM_LIST_PRICE)}
              {UGA_DISCOUNT_BODY_MIDDLE}
              <span className="font-semibold text-foreground">
                {formatUgaPrice(UGA_PREMIUM_DISCOUNTED_PRICE)}
              </span>
              {UGA_DISCOUNT_BODY_CODE}
              <span className="font-mono font-semibold text-foreground">
                {UGA_STUDENT_DISCOUNT_CODE}
              </span>
              {UGA_DISCOUNT_BODY_SUFFIX}
            </p>
            <div className="inline-flex flex-wrap items-center justify-center gap-2 rounded-full bg-secondary/10 px-4 py-2 text-sm font-semibold text-secondary">
              <span>{UGA_STUDENT_DISCOUNT_LABEL}</span>
              <span className="rounded-md bg-background px-2 py-0.5 font-bold font-mono text-foreground">
                {UGA_STUDENT_DISCOUNT_CODE}
              </span>
            </div>
            <Button asChild size="lg" className="w-full sm:w-auto">
              <a
                href={EXTERNAL_LINKS.PREMIUM_MASTERMIND_APPLICATION}
                target="_blank"
                rel="noopener noreferrer"
              >
                {UGA_DISCOUNT_CTA}
                <ArrowRight className="w-4 h-4 ml-2" />
              </a>
            </Button>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-sm">
              <Link
                to="/membership"
                className="text-foreground/60 hover:text-primary transition-colors underline underline-offset-4"
              >
                Compare membership tiers →
              </Link>
              <span className="hidden sm:inline text-foreground/30">·</span>
              <Link
                to={NEXT_COHORT_PATH}
                className="text-foreground/60 hover:text-primary transition-colors underline underline-offset-4"
              >
                Learn about the next cohort →
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default UgaStudentsPage;
